import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  type AlertList,
  type RouteDetail,
  type RouteList,
  type Trip,
  type TripList,
  endpoints,
} from '@viaja/contracts';
import { get } from '../../lib/api';
import { keys } from '../../lib/queries';

function destinationDetail(destination: string) {
  const match = /^\/(rotas|viagens)\/([^/?#]+)\/?(?:[?#].*)?$/.exec(destination);
  if (!match) return null;
  try {
    const id = decodeURIComponent(match[2]!);
    return match[1] === 'rotas' && id === 'nova' ? null : { kind: match[1], id };
  } catch {
    return null;
  }
}

/**
 * Aquece as listas e o destino durante a entrada. Depois prepara até três rotas e
 * duas viagens, incluindo o destino nessa cota. Falhas não impedem a navegação.
 * A promessa inclui esses detalhes; a tela de entrada controla seu próprio prazo.
 */
export async function preloadApp(queryClient: QueryClient, destination: string): Promise<void> {
  const session = queryClient.getQueryData(keys.me);
  if (!session) return;
  // Uma nova sessão (inclusive do mesmo usuário) não herda chamadas encadeadas.
  const isCurrentSession = () => queryClient.getQueryData(keys.me) === session;

  async function warm<T>(queryKey: QueryKey, read: () => Promise<T>): Promise<T | undefined> {
    if (!isCurrentSession()) return undefined;
    try {
      return await queryClient.fetchQuery({
        queryKey,
        staleTime: 30_000,
        retry: false,
        queryFn: async ({ signal }) => {
          if (!isCurrentSession() || signal.aborted) throw new Error('Pré-carregamento encerrado.');
          const data = await read();
          if (!isCurrentSession() || signal.aborted) throw new Error('Pré-carregamento encerrado.');
          return data;
        },
      });
    } catch {
      return undefined;
    }
  }

  const detail = destinationDetail(destination);
  const destinationWork: Promise<unknown>[] = [];
  if (detail?.kind === 'rotas') {
    destinationWork.push(warm(keys.route(detail.id), () => get<RouteDetail>(endpoints.route(detail.id))));
  } else if (detail?.kind === 'viagens') {
    destinationWork.push(
      warm(keys.trip(detail.id), () => get<Trip>(endpoints.trip(detail.id))),
      warm([...keys.routes, { tripId: detail.id }], () =>
        get<RouteList>(`${endpoints.routes}?tripId=${encodeURIComponent(detail.id)}`).then((r) => r.routes),
      ),
    );
  }

  const [routes, trips] = await Promise.all([
    warm(keys.routes, () => get<RouteList>(endpoints.routes).then((r) => r.routes)),
    warm(keys.trips, () => get<TripList>(endpoints.trips).then((r) => r.trips)),
    Promise.all([
      warm(keys.alerts, () => get<AlertList>(endpoints.alerts).then((r) => r.alerts)),
      ...destinationWork,
    ]),
  ]);

  if (!isCurrentSession()) return;
  const routeIds = [
    ...(detail?.kind === 'rotas' ? [detail.id] : []),
    ...(routes?.map((route) => route.id) ?? []),
  ];
  const tripIds = [
    ...(detail?.kind === 'viagens' ? [detail.id] : []),
    ...(trips?.map((trip) => trip.id) ?? []),
  ];
  await Promise.all([
    ...[...new Set(routeIds)]
      .slice(0, 3)
      .filter((id) => detail?.kind !== 'rotas' || id !== detail.id)
      .map((id) => warm(keys.route(id), () => get<RouteDetail>(endpoints.route(id)))),
    ...[...new Set(tripIds)]
      .slice(0, 2)
      .filter((id) => detail?.kind !== 'viagens' || id !== detail.id)
      .map((id) => warm(keys.trip(id), () => get<Trip>(endpoints.trip(id)))),
  ]);
}
